import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Ticket, Gift, Trash2, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const card = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";
const inputCls = "rounded-lg border border-brand-line dark:border-neutral-700 bg-transparent px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none";

export default function Memberships() {
  const [bundles, setBundles] = useState([]);
  const [promos, setPromos] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [promo, setPromo] = useState({ code: "", percent_off: 10 });
  const [gift, setGift] = useState({ code: "", amount: 50 });

  const load = () => {
    api.get("/bundles").then((r) => setBundles(r.data)).catch(() => {});
    api.get("/admin/promo-codes").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/admin/gift-cards").then((r) => setGifts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addPromo = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/promo-codes", { ...promo, percent_off: Number(promo.percent_off) }); toast.success("Promo created"); setPromo({ code: "", percent_off: 10 }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const addGift = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/gift-cards", { ...gift, amount: Number(gift.amount) }); toast.success("Gift card created"); setGift({ code: "", amount: 50 }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delPromo = async (id) => { await api.delete(`/admin/promo-codes/${id}`); load(); };
  const totalPacks = bundles.reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div data-testid="memberships-page">
      <h1 className="font-display text-4xl">Memberships</h1>
      <p className="text-brand-ink/60 dark:text-white/50 mt-2">Session packs, promo codes and gift cards.</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <Stat label="Packs sold" value={bundles.length} />
        <Stat label="Pack revenue" value={`$${totalPacks.toFixed(2)}`} />
        <Stat label="Active promos" value={promos.filter((p) => p.active).length} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className={`${card} p-6`}>
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Package className="w-5 h-5 text-brand-red" /> Purchased packs</h2>
          {bundles.length === 0 ? <p className="text-brand-ink/50 dark:text-white/40" data-testid="packs-empty">No packs purchased yet.</p> : (
            <div className="divide-y divide-brand-line dark:divide-neutral-800">
              {bundles.map((b) => (
                <div key={b.session_id} className="py-3 flex items-center justify-between" data-testid={`pack-${b.session_id}`}>
                  <div><div className="font-medium">{b.package_name}</div><div className="text-sm text-brand-ink/50 dark:text-white/40">{b.email}</div></div>
                  <span className="text-sm">{b.remaining}/{b.sessions} left</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className={`${card} p-6`}>
            <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Ticket className="w-5 h-5 text-brand-red" /> Promo codes</h2>
            <form onSubmit={addPromo} className="flex gap-2 mb-4 flex-wrap">
              <input placeholder="CODE" value={promo.code} onChange={(e) => setPromo({ ...promo, code: e.target.value })} required className={`${inputCls} flex-1`} data-testid="promo-code" />
              <input type="number" placeholder="% off" value={promo.percent_off} onChange={(e) => setPromo({ ...promo, percent_off: e.target.value })} className={`${inputCls} w-24`} data-testid="promo-percent" />
              <button className="px-4 py-2 rounded-lg bg-brand-red text-white text-sm" data-testid="promo-add"><Plus className="w-4 h-4" /></button>
            </form>
            <div className="space-y-2">
              {promos.map((p) => (
                <div key={p.id} className="flex items-center justify-between bg-brand-mist dark:bg-neutral-800 rounded-lg px-4 py-2 text-sm" data-testid={`promo-${p.id}`}>
                  <span className="font-mono font-semibold">{p.code}</span><span>{p.percent_off}% off</span>
                  <button onClick={() => delPromo(p.id)} className="text-brand-ink/40 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className={`${card} p-6`}>
            <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Gift className="w-5 h-5 text-brand-red" /> Gift cards</h2>
            <form onSubmit={addGift} className="flex gap-2 mb-4 flex-wrap">
              <input placeholder="CODE" value={gift.code} onChange={(e) => setGift({ ...gift, code: e.target.value })} required className={`${inputCls} flex-1`} data-testid="gift-code" />
              <input type="number" placeholder="$" value={gift.amount} onChange={(e) => setGift({ ...gift, amount: e.target.value })} className={`${inputCls} w-24`} data-testid="gift-amount" />
              <button className="px-4 py-2 rounded-lg bg-brand-red text-white text-sm" data-testid="gift-add"><Plus className="w-4 h-4" /></button>
            </form>
            <div className="space-y-2">
              {gifts.map((g) => (
                <div key={g.id} className="flex items-center justify-between bg-brand-mist dark:bg-neutral-800 rounded-lg px-4 py-2 text-sm" data-testid={`gift-${g.id}`}>
                  <span className="font-mono font-semibold">{g.code}</span><span>${g.balance} / ${g.amount}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const cardCls = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";
const Stat = ({ label, value }) => (
  <div className={`${cardCls} p-5`}><div className="text-xs uppercase tracking-[0.15em] text-brand-ink/50 dark:text-white/40">{label}</div><div className="mt-3 font-display text-3xl">{value}</div></div>
);
